import React from 'react';
import { DIAGRAM_TAG, CalloutPriority, estimateStyle, MatchLevel, matchTagStyle } from '../../utils/diagramTag';

const FONT = 'Inter Tight, system-ui, -apple-system, sans-serif';

export interface DiagramRefChipProps {
  /** Ref number shown inside the circle (the callout label). */
  number: string;
  priority: CalloutPriority;
  /** Rank-1 hero gets the ★ tucked top-left inside the filled circle. Ignored
   *  when `estimated` (v2.1 §3: no ★ anywhere in the estimated state). */
  star?: boolean;
  /** Amber estimate family instead of the navy ramp, mirroring the diagram tag
   *  so the chip and its callout match. */
  estimated?: boolean;
  /** The estimate behind `estimated`: shades the amber fill, so a weak match
   *  draws pale and a strong one deep. Omitted = flat amber. */
  score?: number | null;
  /** Match-strength tier (1–4). When set it wins over `estimated`/`score` and
   *  draws the same tier fill as the diagram viewer's tags. */
  level?: MatchLevel | null;
  /** Green satellite ✓ badge at the top-right (in-cart status overlay). */
  inCart?: boolean;
  /** Chip diameter in px (default 26, per guidelines §2.2 Ref column). */
  size?: number;
}

/**
 * Results-table reference chip — a circle that MIRRORS the diagram tag styling
 * exactly (Catalog Parts Illustration Guidelines §2.2 / §3 / §4) so the table row
 * and the diagram callout read as the same object. Priority is the navy ramp
 * (hero fill / secondary indigo-outline / optional gray-outline / na
 * dashed+slash+45%); rank-1 hero carries the ★; in-cart overlays a green
 * satellite ✓ badge at the top-right, ringed in white so it stays legible over
 * any priority fill. Selection is a ROW treatment, not on the chip.
 */
const DiagramRefChip: React.FC<DiagramRefChipProps> = ({ number, priority, star, estimated, score, level, inCart, size = 26 }) => {
  const na = priority === 'na';
  // `na` outranks the estimate state: an absent part is not a low-confidence one.
  const tier = !na && level != null ? matchTagStyle(level) : null;
  const est = (!!estimated || !!tier) && !na;
  const estStyle = tier ?? { ...estimateStyle(score), strokeWidth: 1.5, dash: undefined as string | undefined };
  const cy = size / 2;
  const r = cy - 1.5; // half-height of the pill (inset leaves room for the stroke)
  const fontSize = Math.round(size * 0.42);
  // Short refs stay a circle; long ones (Hyundai/Kia codes are 5–6 chars) grow into
  // a horizontal stadium so the number never overflows — same treatment as the
  // diagram tag. Width is estimated from the glyph advance (~0.6·fontSize, bold
  // Inter Tight), sized so the whole ref fits with a little breathing room.
  const textHalf = (number ? number.length : 0) * fontSize * 0.6 / 2;
  const halfW = Math.max(r, textHalf + size * 0.19);
  const boxW = Math.round(halfW * 2 + 3); // + the same 1.5px inset on each side
  const cx = boxW / 2;
  const fg = est
    ? estStyle.fg
    : priority === 'hero' ? DIAGRAM_TAG.white
      : priority === 'secondary' ? DIAGRAM_TAG.secFg
        : na ? DIAGRAM_TAG.naFg : DIAGRAM_TAG.optFg;
  const pillProps = { x: cx - halfW, y: cy - r, width: halfW * 2, height: r * 2, rx: r, ry: r };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, width: boxW, height: size }}>
      <svg width={boxW} height={size} viewBox={`0 0 ${boxW} ${size}`} style={{ overflow: 'visible', display: 'block' }} aria-hidden="true">
        <g opacity={na ? 0.45 : 1}>
          {est
            ? <rect {...pillProps} fill={estStyle.fill} stroke={estStyle.stroke} strokeWidth={estStyle.strokeWidth} strokeDasharray={estStyle.dash} />
            : priority === 'hero'
            ? <rect {...pillProps} fill={DIAGRAM_TAG.heroBg} />
            : na
              ? <rect {...pillProps} fill={DIAGRAM_TAG.naFill} stroke={DIAGRAM_TAG.naStroke} strokeWidth={1} strokeDasharray="3 2" />
              : <rect {...pillProps} fill={DIAGRAM_TAG.white} stroke={priority === 'secondary' ? DIAGRAM_TAG.secStroke : DIAGRAM_TAG.optStroke} strokeWidth={1.5} />}
          <text x={cx} y={cy} fill={fg} fontSize={fontSize} fontWeight={700} textAnchor="middle" dominantBaseline="central" fontFamily={FONT}>{number}</text>
          {star && priority === 'hero' && !est && (
            <text x={cx - halfW + fontSize * 0.5} y={cy - fontSize * 0.42} fill={DIAGRAM_TAG.white} fontSize={fontSize * 0.62} textAnchor="middle" dominantBaseline="central" fontFamily={FONT}>★</text>
          )}
          {na && <line x1={cx - halfW + r * 0.34} y1={cy + r * 0.66} x2={cx + halfW - r * 0.34} y2={cy - r * 0.66} stroke={DIAGRAM_TAG.naFg} strokeWidth={1.4} strokeLinecap="round" />}
        </g>
      </svg>
      {inCart && (
        <span
          style={{
            position: 'absolute', top: -6, right: -6,
            width: 16, height: 16, borderRadius: '50%',
            background: DIAGRAM_TAG.cart, border: `2px solid ${DIAGRAM_TAG.white}`,
            boxShadow: '0 1px 3px rgba(32,30,26,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width={8} height={8} viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth={2.5}>
            <path d="M2.5 7.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </span>
  );
};

export default DiagramRefChip;
